import { useMemo } from "react";

import { compact, sortBy } from "lodash";

import { Autocomplete } from "@components/v2";
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import { arrayMove, SortableContext } from "@dnd-kit/sortable";
import { useBreakdownDimensions } from "@features/sales-v4/hooks";

import { BreakdownItem } from "./breakdown-item";

interface Props {
  selection: string[];
  onChange: (val: string[]) => void;
}

export const Breakdowns = ({ selection, onChange }: Props) => {
  const dimensions = useBreakdownDimensions();
  const sensors = useSensors(useSensor(PointerSensor));

  const options = useMemo(() => {
    return sortBy(
      dimensions
        .filter((e) => !selection.includes(e.id))
        .map((e) => ({ label: e.name, value: e.id })),
      ["label"],
    );
  }, [selection, dimensions]);

  const selectedItems = useMemo(() => {
    return compact(selection.map((e) => dimensions.find((d) => d.id === e)));
  }, [selection, dimensions]);

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    const oldIndex = selection.findIndex((item) => item === active.id);
    const newIndex = selection.findIndex((item) => item === over?.id);

    const newOrdering = arrayMove(selection, oldIndex, newIndex);

    onChange(newOrdering);
  };

  return (
    <div>
      <div className="text-xs font-semibold mb-2">
        Breakdowns (drag to reorder)
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToParentElement]}
        >
          <SortableContext items={selectedItems.map((e) => e.id)}>
            {selectedItems.map((item) => (
              <BreakdownItem
                key={item.id}
                id={item.id}
                label={item.name}
                onRemove={() => {
                  onChange(selection.filter((e) => e !== item.id));
                }}
              />
            ))}
          </SortableContext>
        </DndContext>
        <Autocomplete
          size="md"
          placeholder="Add Breakdown"
          inputWrapperClassName="w-48"
          dropdownClassName="w-72"
          value={null}
          options={options}
          onChange={(val) => {
            if (val) {
              onChange([...selection, val]);
            }
          }}
        />
      </div>
    </div>
  );
};
